import { useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, MapPinned, RadioTower, Search } from 'lucide-react';
import { request } from '../api';
import type { SimState } from '../domain/model';
import type { ControlCenterResult, ControlCenterSummary } from '../domain/controlCenters';

const federalStates = [
  'Baden-Württemberg',
  'Bayern',
  'Berlin',
  'Brandenburg',
  'Bremen',
  'Hamburg',
  'Hessen',
  'Mecklenburg-Vorpommern',
  'Niedersachsen',
  'Nordrhein-Westfalen',
  'Rheinland-Pfalz',
  'Saarland',
  'Sachsen',
  'Sachsen-Anhalt',
  'Schleswig-Holstein',
  'Thüringen',
];

export default function ControlCenterSelector({
  state,
  enabled,
  onSelect,
  onMap,
}: {
  state: SimState;
  enabled: boolean;
  onSelect: (id: string) => Promise<boolean>;
  onMap: () => void;
}) {
  const [query, setQuery] = useState(''),
    [federalState, setFederalState] = useState(''),
    [result, setResult] = useState<ControlCenterResult | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ q: query });
      if (federalState) params.set('state', federalState);
      request<ControlCenterResult>(`control-centers?${params}`)
        .then((data) => {
          if (!cancelled) {
            setResult(data);
            setError('');
          }
        })
        .catch((reason) => {
          if (!cancelled) setError(reason.message);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, federalState]);

  const active = state.area.controlCenterId;
  return (
    <section className="control-center-selector" aria-labelledby="control-center-title">
      <div className="control-center-heading">
        <div className="country-icon">
          <RadioTower size={27} />
        </div>
        <div>
          <h2 id="control-center-title">Leitstelle & Zuständigkeitsbereich</h2>
          <p>
            Wähle den 112-Arbeitsplatz. Einsatzgebiet, erfasste Wachen, Organisationen und
            simulierte Einheiten werden automatisch geladen.
          </p>
        </div>
        {active && (
          <button className="button secondary" onClick={onMap}>
            <MapPinned size={16} />
            Gebietskarte
          </button>
        )}
      </div>
      {active ? (
        <div className="active-control-center">
          <CheckCircle2 size={20} />
          <div>
            <span>Aktiver Arbeitsplatz</span>
            <b>{state.area.name}</b>
            <small>{state.area.jurisdictions?.join(' · ')}</small>
          </div>
          <em>{state.area.federalState}</em>
        </div>
      ) : (
        <div className="control-center-hint">
          Aktuell ist ein freier Übungsbereich aktiv. Wähle eine Leitstelle für einen realen
          Kreiszuschnitt.
        </div>
      )}
      <div className="control-center-filters">
        <div className="search-field">
          <Search size={16} />
          <input
            aria-label="Leitstelle suchen"
            placeholder="Leitstelle, Stadt oder Landkreis suchen …"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <select
          aria-label="Bundesland filtern"
          value={federalState}
          onChange={(event) => setFederalState(event.target.value)}
        >
          <option value="">Alle Bundesländer</option>
          {federalStates.map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>
      </div>
      <div className="control-center-count">
        {loading
          ? 'Leitstellen werden geladen …'
          : `${result?.total ?? 0} kombinierte Feuerwehr-/Rettungsdienst-Leitstellen`}
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="control-center-list">
        {result?.entries.slice(0, 50).map((entry: ControlCenterSummary) => (
          <article className={entry.id === active ? 'active' : ''} key={entry.id}>
            <div className="control-center-copy">
              <div>
                <span>{entry.federalState}</span>
                <i>{entry.sourceKind}</i>
              </div>
              <h3>{entry.name}</h3>
              <p>{entry.jurisdictions.map((area) => area.name).join(' · ')}</p>
              <small>
                {entry.areaKm2
                  ? `${entry.areaKm2.toLocaleString('de-DE')} km²`
                  : 'Fläche ohne Angabe'}
                {entry.population
                  ? ` · ${entry.population.toLocaleString('de-DE')} Einwohner laut Verzeichnis`
                  : ''}
                {' · '}
                {entry.tasks.join(', ')}
              </small>
            </div>
            <div className="control-center-actions">
              <a
                href={entry.sourceUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={`${entry.name} Quelle`}
                title="Quelle öffnen"
              >
                <ExternalLink size={14} />
              </a>
              <button
                className="button primary"
                disabled={!enabled || entry.id === active}
                onClick={() => onSelect(entry.id)}
              >
                {entry.id === active ? 'Aktiv' : 'Leitstelle wählen'}
              </button>
            </div>
          </article>
        ))}
        {!loading && result && result.total > 50 && (
          <div className="control-center-more">
            50 von {result.total} angezeigt · Suche oder Bundesland eingrenzen
          </div>
        )}
        {!loading && result && !result.total && (
          <div className="empty">Keine passende Leitstelle gefunden.</div>
        )}
      </div>
      <p className="control-center-source">
        Gebietsgrenzen: © BKG (2026), Datenstand 01.01.2025, dl-de/by-2-0. Zuordnung: aktueller
        Leitstellenverzeichnis-Abgleich; Ludwigsburg und Heilbronn sind zusätzlich an offiziellen
        Betreiberangaben geprüft. Standorte stammen aus dem lokalen öffentlichen Katalog und
        redaktionell geprüften Ergänzungen. Veröffentlichte Fahrzeugbestände sind mit Quellen
        belegt; übrige Fahrzeuge und die Verfügbarkeit bleiben simuliert. Operative
        Sonderzuständigkeiten und kurzfristige Kooperationen können abweichen.
      </p>
    </section>
  );
}
